<template>
  <div class="flex flex-col items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    
    <!-- Drag Overlay -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-red-600/90 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-6 rounded-full mb-4 backdrop-blur-sm shadow-xl">
           <Scissors :size="56" class="text-white" />
        </div>
        <h2 class="text-white text-3xl font-bold tracking-tight">Drop PDF file to Split</h2>
      </div>
    </transition>

    <!-- Dropzone (Initial Upload State) -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-4xl bg-gradient-to-b from-red-50 to-white hover:from-white hover:to-red-50 mt-4 transition-all duration-300 cursor-pointer shadow-md rounded-xl flex flex-col items-center justify-center text-center py-10 px-6 relative overflow-hidden border-2 border-dashed border-red-300"
    >
      <div class="relative z-10 flex flex-col items-center">
        <div class="mx-auto w-16 h-16 flex items-center justify-center mb-3">
          <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/pdf%20%282%29-g2dPLzFcgQFJYtGGxU0T9PMNB8UPUx.png" alt="Split PDF" class="w-full h-full object-contain" />
        </div>
        
        <!-- Upload Button -->
        <button
          @click.stop="fileInput.click()"
          class="bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-2.5 rounded-lg active:bg-red-700 transition-all duration-200 flex items-center gap-2 shadow text-sm">
          <Upload :size="18" />
          <span>{{ t('select_pdf_files') }}</span>
        </button>
        
        <p class="mb-2 font-semibold text-sm text-gray-700 mt-4">
          {{ t('or_drop_pdf') }}
        </p>
        <p class="text-xs text-gray-500 font-medium">
          {{ t('paste_hint') }}
        </p>
      </div>
    </div>

    <!-- Main Card Workspace (Sleek Side-By-Side Layout) -->
    <div v-else class="bg-white rounded-xl w-full max-w-6xl shadow-lg border border-gray-200 mt-2 overflow-hidden">
      
      <!-- Top Bar / File Header -->
      <div class="bg-gradient-to-r from-red-50 via-white to-red-50 px-4 py-3 border-b border-red-100 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div class="flex items-center gap-2.5">
          <div class="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center text-red-600 font-bold shadow-xs">
            <Scissors :size="18" />
          </div>
          <div>
            <h3 class="font-bold text-gray-900 text-sm truncate max-w-xs md:max-w-sm">
              {{ currentFileName }}
            </h3>
            <p class="text-gray-500 font-medium text-[11px]">
              Total Pages: <span class="font-bold text-red-600">{{ pages.length }}</span>
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button @click="fileInput.click()" class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition shadow-xs">
            <Plus :size="14" />
            {{ t('add_more') }}
          </button>
          <button @click="resetAll" class="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition">
            <RotateCcw :size="14" />
            {{ t('clear') }}
          </button>
        </div>
      </div>

      <!-- Main Layout Grid (Left Visual Area 8 Cols + Right Sidebar 4 Cols Side-by-Side) -->
      <div class="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 min-h-[460px]">
        
        <!-- LEFT AREA: Visual PDF Page Thumbnails Area (8 Cols) -->
        <div class="lg:col-span-8 p-4 bg-gray-50/50 overflow-y-auto max-h-[580px]">
          
          <!-- TAB 1: RANGE MODE VISUAL DISPLAY (RANGES DISPLAYED SIDE-BY-SIDE!) -->
          <div v-if="mainTab === 'range'" class="space-y-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <Sliders :size="15" class="text-red-500" />
                <span>Ranges Side-by-Side Preview</span>
              </span>
              <span class="text-[11px] text-gray-500 font-semibold bg-white px-2.5 py-0.5 rounded-full border border-gray-200 shadow-xs">
                {{ computedRanges.length }} Range(s)
              </span>
            </div>

            <!-- Grouped Range Cards Displayed SIDE-BY-SIDE (Flex Row / Grid) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div 
                v-for="(range, rIdx) in computedRanges" 
                :key="'range-' + rIdx"
                class="bg-white p-3.5 rounded-xl border border-red-200 shadow-xs space-y-2 flex flex-col justify-between h-full">
                
                <div class="flex items-center justify-between pb-1.5 border-b border-gray-100 text-xs">
                  <div class="flex items-center gap-1.5">
                    <span class="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                      {{ rIdx + 1 }}
                    </span>
                    <span class="font-bold text-gray-800">
                      Range {{ rIdx + 1 }}: Pages {{ range.from }} - {{ range.to }}
                    </span>
                  </div>
                  <button 
                    v-if="rangeMode === 'custom' && customRanges.length > 1" 
                    @click="removeCustomRange(rIdx)" 
                    class="text-red-500 hover:text-red-700 text-[11px] font-semibold flex items-center gap-1 bg-red-50 px-2 py-0.5 rounded transition shrink-0">
                    <XCircle :size="12" />
                    <span>Remove</span>
                  </button>
                </div>

                <!-- Page Thumbnails Inside Range (Side-by-Side Grid) -->
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                  <div 
                    v-for="pageObj in range.pages" 
                    :key="'p-' + pageObj.pageNum" 
                    class="relative bg-white rounded-lg border border-gray-200 shadow-2xs flex flex-col items-center p-1.5 group hover:border-red-300 transition">
                    
                    <div class="aspect-[3/4] w-full bg-gray-50 rounded overflow-hidden flex items-center justify-center p-0.5">
                      <img 
                        v-if="pageObj.thumbnail"
                        :src="pageObj.thumbnail"
                        class="max-h-24 max-w-full object-contain"
                        alt="Page Thumbnail"
                      />
                      <div v-else class="animate-spin rounded-full h-4 w-4 border-2 border-red-200 border-t-red-600"></div>
                    </div>

                    <div class="w-full text-center py-0.5 mt-1 bg-gray-100 rounded text-[10px] font-bold text-gray-600">
                      {{ t('page') }} {{ pageObj.pageNum }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB 2: EXTRACT PAGES VISUAL DISPLAY GRID (SIDE-BY-SIDE GRID) -->
          <div v-else-if="mainTab === 'pages'" class="space-y-3">
            <div class="bg-white p-3 rounded-lg border border-gray-200 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold shadow-2xs">
              <span class="text-gray-700 flex items-center gap-1.5">
                <FileCheck :size="15" class="text-red-500" />
                <span>Click thumbnails to toggle page selection:</span>
              </span>
              <div class="flex items-center gap-2 text-[11px]">
                <button @click="selectAllPages" class="text-red-600 hover:underline font-bold">Select All</button>
                <span class="text-gray-300">&middot;</span>
                <button @click="deselectAllPages" class="text-gray-500 hover:underline font-bold">Deselect All</button>
              </div>
            </div>

            <!-- Page Selector Grid (Side-by-Side Grid) -->
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              <div 
                v-for="page in pages" 
                :key="'extract-' + page.pageNum"
                @click="page.selected = !page.selected"
                class="cursor-pointer group select-none">
                <div 
                  class="bg-white rounded-lg border-2 relative transition-all duration-150 overflow-hidden p-1.5 flex flex-col items-center justify-between"
                  :class="page.selected ? 'border-red-500 ring-2 ring-red-500/20 shadow-xs bg-red-50/20' : 'border-gray-200 opacity-60 hover:opacity-100'">
                  
                  <div 
                    class="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center shadow-xs z-20 text-[9px] font-bold"
                    :class="page.selected ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400'">
                    <Check :size="11" />
                  </div>

                  <div class="aspect-[3/4] w-full bg-gray-50 rounded overflow-hidden flex items-center justify-center p-0.5 my-0.5">
                    <img v-if="page.thumbnail" :src="page.thumbnail" class="max-h-24 max-w-full object-contain" />
                    <div v-else class="animate-spin rounded-full h-4 w-4 border-2 border-red-200 border-t-red-600"></div>
                  </div>

                  <div 
                    class="w-full text-center py-0.5 rounded text-[10px] font-bold transition-colors"
                    :class="page.selected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600'">
                    {{ t('page') }} {{ page.pageNum }}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- RIGHT SIDEBAR: Sleek Options, Inputs & Action Button (4 Cols Side-by-Side with Left Canvas) -->
        <div class="lg:col-span-4 p-4 bg-white flex flex-col justify-between space-y-4 text-xs">
          <div class="space-y-4">
            <h4 class="text-sm font-extrabold text-gray-900 border-b border-gray-100 pb-2">
              {{ t('split_mode') }}
            </h4>

            <!-- Main Mode Tabs Switcher -->
            <div class="grid grid-cols-2 gap-1.5 bg-gray-100 p-1 rounded-lg border border-gray-200 font-bold text-xs">
              <button
                @click="mainTab = 'range'"
                class="py-1.5 px-2 rounded-md transition-all flex items-center justify-center gap-1"
                :class="mainTab === 'range' ? 'bg-white text-red-600 shadow-xs' : 'text-gray-600 hover:text-gray-900'">
                <Sliders :size="14" />
                <span>{{ t('mode_range') }}</span>
              </button>
              <button
                @click="mainTab = 'pages'"
                class="py-1.5 px-2 rounded-md transition-all flex items-center justify-center gap-1"
                :class="mainTab === 'pages' ? 'bg-white text-red-600 shadow-xs' : 'text-gray-600 hover:text-gray-900'">
                <FileCheck :size="14" />
                <span>{{ t('mode_extract') }}</span>
              </button>
            </div>

            <!-- MODE 1: RANGE MODE SIDEBAR CONTROLS -->
            <div v-if="mainTab === 'range'" class="space-y-4">
              <div>
                <label class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Range Mode:</label>
                <div class="grid grid-cols-2 gap-1.5">
                  <button
                    @click="rangeMode = 'custom'"
                    class="py-1.5 px-2 rounded-lg border-2 font-bold text-xs transition-all"
                    :class="rangeMode === 'custom' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-300'">
                    {{ t('submode_custom_range') }}
                  </button>
                  <button
                    @click="rangeMode = 'fixed'"
                    class="py-1.5 px-2 rounded-lg border-2 font-bold text-xs transition-all"
                    :class="rangeMode === 'fixed' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-300'">
                    {{ t('submode_fixed_range') }}
                  </button>
                </div>
              </div>

              <!-- CUSTOM RANGES INPUTS -->
              <div v-if="rangeMode === 'custom'" class="space-y-2.5">
                <div 
                  v-for="(cr, cIdx) in customRanges" 
                  :key="'cr-side-' + cIdx"
                  class="p-2.5 bg-gray-50 rounded-lg border border-gray-200 space-y-1.5 relative">
                  <div class="flex justify-between items-center text-[11px] font-bold text-gray-800">
                    <span>Range {{ cIdx + 1 }}</span>
                    <button v-if="customRanges.length > 1" @click="removeCustomRange(cIdx)" class="text-red-500 hover:underline text-[10px] font-semibold">Remove</button>
                  </div>
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <label class="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">{{ t('from_page') }}</label>
                      <input type="number" min="1" :max="pages.length" v-model.number="cr.from" class="w-full px-2 py-1 rounded border border-gray-300 text-xs font-bold text-gray-800 focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none" />
                    </div>
                    <div>
                      <label class="text-[9px] font-bold text-gray-400 uppercase block mb-0.5">{{ t('to_page') }}</label>
                      <input type="number" min="1" :max="pages.length" v-model.number="cr.to" class="w-full px-2 py-1 rounded border border-gray-300 text-xs font-bold text-gray-800 focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none" />
                    </div>
                  </div>
                </div>

                <button @click="addCustomRange" class="w-full py-1.5 border border-dashed border-gray-300 rounded-lg text-gray-600 font-bold text-xs hover:border-red-500 hover:text-red-600 transition-colors flex items-center justify-center gap-1">
                  <Plus :size="14" />
                  <span>{{ t('add_range') }}</span>
                </button>
              </div>

              <!-- FIXED RANGE INPUT -->
              <div v-else class="space-y-2.5">
                <div class="flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                  <span class="text-xs font-bold text-gray-700">{{ t('fixed_range_label') }}</span>
                  <input type="number" min="1" :max="pages.length" v-model.number="fixedPageSize" class="w-14 px-2 py-1 rounded border border-gray-300 text-xs font-bold text-center text-gray-800 outline-none focus:ring-1 focus:ring-red-500" />
                </div>

                <div class="bg-red-50 border border-red-200 p-2.5 rounded-lg flex items-start gap-1.5 text-[11px] text-red-800">
                  <Info :size="14" class="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    {{ t('fixed_range_summary', { size: fixedPageSize }) }}<br/>
                    <strong class="block mt-0.5 font-bold">{{ computedRanges.length }} PDF files will be created.</strong>
                  </div>
                </div>
              </div>

              <!-- Merge Ranges Option -->
              <label class="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer select-none">
                <input type="checkbox" v-model="mergeAllRanges" class="w-3.5 h-3.5 text-red-600 rounded border-gray-300 focus:ring-red-500" />
                <span class="text-xs font-bold text-gray-700">{{ t('merge_ranges') }}</span>
              </label>
            </div>

            <!-- MODE 2: EXTRACT PAGES SIDEBAR CONTROLS -->
            <div v-else class="space-y-3">
              <div class="space-y-1.5">
                <label 
                  @click="extractMode = 'selected'"
                  class="flex items-center gap-2.5 p-2.5 rounded-lg border-2 cursor-pointer transition-all"
                  :class="extractMode === 'selected' ? 'border-red-500 bg-red-50/40' : 'border-gray-200 bg-white'">
                  <input type="radio" value="selected" v-model="extractMode" class="text-red-600 focus:ring-red-500" />
                  <div>
                    <h4 class="font-bold text-xs text-gray-900">{{ t('submode_select_pages') }}</h4>
                    <p class="text-[10px] text-gray-500 mt-0.5">Extract checked page thumbnails</p>
                  </div>
                </label>

                <label 
                  @click="extractMode = 'all'"
                  class="flex items-center gap-2.5 p-2.5 rounded-lg border-2 cursor-pointer transition-all"
                  :class="extractMode === 'all' ? 'border-red-500 bg-red-50/40' : 'border-gray-200 bg-white'">
                  <input type="radio" value="all" v-model="extractMode" class="text-red-600 focus:ring-red-500" />
                  <div>
                    <h4 class="font-bold text-xs text-gray-900">{{ t('submode_extract_all') }}</h4>
                    <p class="text-[10px] text-gray-500 mt-0.5">Separate all pages into individual PDFs</p>
                  </div>
                </label>
              </div>

              <div class="bg-red-50 border border-red-200 p-2.5 rounded-lg text-[11px] text-red-800 flex items-start gap-1.5">
                <Info :size="14" class="text-red-500 shrink-0 mt-0.5" />
                <span v-if="extractMode === 'selected'">Selected <strong>{{ selectedPageNumbers.length }}</strong> page(s) will be extracted.</span>
                <span v-else>All <strong>{{ pages.length }}</strong> pages will be saved into a ZIP archive.</span>
              </div>
            </div>
          </div>

          <!-- Bottom Action Button in Sidebar -->
          <div class="pt-3 border-t border-gray-100">
            <button 
              @click="processSplit" 
              :disabled="isProcessing || (mainTab === 'pages' && extractMode === 'selected' && selectedPageNumbers.length === 0)"
              class="w-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-2.5 px-4 rounded-lg shadow hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <div v-if="isProcessing" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>{{ isProcessing ? t('splitting_pdf') : t('split_pdf_btn') }}</span>
              <ArrowRight v-if="!isProcessing" :size="16" />
            </button>
          </div>

        </div>

      </div>
    </div>

    <!-- Hidden File Input -->
    <input type="file" ref="fileInput" accept="application/pdf" class="hidden" @change="onFileChange" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, shallowRef, markRaw, onMounted, onUnmounted } from 'vue'
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import JSZip from 'jszip'
import { Scissors, Upload, Sliders, FileCheck, RotateCcw, XCircle, Check, Plus, Info, ArrowRight } from 'lucide-vue-next'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const props = defineProps({
  translations: {
    type: Object,
    default: () => ({})
  }
})

const t = (key, replacements = {}) => {
  let translation = props.translations[key] || key
  for (const [k, v] of Object.entries(replacements)) {
    translation = translation.replace(`:${k}`, v)
  }
  return translation
}

const pages = ref([])
const pdfLibDoc = shallowRef(null)
const pdfjsDoc = shallowRef(null)
const currentFileName = ref('')
const isDragging = ref(false)
const isProcessing = ref(false)
const fileInput = ref(null)

// Navigation & Mode Tabs
const mainTab = ref('range') // 'range' | 'pages'
const rangeMode = ref('custom') // 'custom' | 'fixed'
const extractMode = ref('selected') // 'selected' | 'all'

// Options
const mergeAllRanges = ref(false)
const fixedPageSize = ref(1)
const customRanges = ref([
  { from: 1, to: 1 }
])

const selectedPageNumbers = computed(() => {
  return pages.value.filter(p => p.selected).map(p => p.pageNum)
})

// Compute Range page groups dynamically with REACTIVE page objects
const computedRanges = computed(() => {
  if (pages.value.length === 0) return []
  
  if (rangeMode.value === 'custom') {
    const list = []
    customRanges.value.forEach(cr => {
      const from = Math.max(1, Math.min(cr.from || 1, pages.value.length))
      const to = Math.max(from, Math.min(cr.to || from, pages.value.length))
      const pList = []
      for (let i = from; i <= to; i++) {
        const pObj = pages.value.find(x => x.pageNum === i)
        if (pObj) pList.push(pObj)
      }
      list.push({ from, to, pages: pList })
    })
    return list
  } else {
    // Fixed ranges
    const step = Math.max(1, fixedPageSize.value || 1)
    const list = []
    for (let i = 1; i <= pages.value.length; i += step) {
      const from = i
      const to = Math.min(i + step - 1, pages.value.length)
      const pList = []
      for (let k = from; k <= to; k++) {
        const pObj = pages.value.find(x => x.pageNum === k)
        if (pObj) pList.push(pObj)
      }
      list.push({ from, to, pages: pList })
    }
    return list
  }
})

const addCustomRange = () => {
  const last = customRanges.value[customRanges.value.length - 1]
  const nextFrom = last ? Math.min(last.to + 1, pages.value.length) : 1
  const nextTo = Math.min(nextFrom + 1, pages.value.length)
  customRanges.value.push({ from: nextFrom, to: nextTo })
}

const removeCustomRange = (idx) => {
  if (customRanges.value.length > 1) {
    customRanges.value.splice(idx, 1)
  }
}

const selectAllPages = () => pages.value.forEach(p => p.selected = true)
const deselectAllPages = () => pages.value.forEach(p => p.selected = false)

let uidCounter = 0

const renderPageThumbnail = async (pdfDoc, pageNum) => {
  try {
    const page = await pdfDoc.getPage(pageNum)
    const viewport = page.getViewport({ scale: 1.2, rotation: 0 })
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvasContext: ctx, viewport }).promise
    return canvas.toDataURL("image/png")
  } catch (err) {
    console.error(`Page ${pageNum} render error:`, err)
    const fallbackCanvas = document.createElement("canvas")
    fallbackCanvas.width = 120
    fallbackCanvas.height = 160
    const ctx = fallbackCanvas.getContext("2d")
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, 120, 160)
    ctx.fillStyle = "#F43230"
    ctx.font = "bold 14px sans-serif"
    ctx.textAlign = "center"
    ctx.fillText(`Page ${pageNum}`, 60, 80)
    return fallbackCanvas.toDataURL("image/png")
  }
}

const processFile = async (file) => {
  if (file.type !== 'application/pdf') return
  currentFileName.value = file.name
  const buffer = await file.arrayBuffer()
  
  const loadedPdfLib = await PDFDocument.load(buffer)
  pdfLibDoc.value = markRaw(loadedPdfLib)
  
  try {
    const uint8 = new Uint8Array(buffer.slice(0))
    const loadedPdfjs = await pdfjsLib.getDocument({ data: uint8 }).promise
    pdfjsDoc.value = markRaw(loadedPdfjs)
  } catch (e) {
    console.error('PDFJS load error:', e)
  }

  const totalP = pdfjsDoc.value ? pdfjsDoc.value.numPages : pdfLibDoc.value.getPageCount()
  const newPages = []
  for (let i = 1; i <= totalP; i++) {
    newPages.push({
      uid: uidCounter++,
      pageNum: i,
      selected: true,
      thumbnail: '',
      isLoading: true
    })
  }

  pages.value = newPages

  // Default ranges Setup
  const half = Math.max(1, Math.floor(totalP / 2))
  customRanges.value = [
    { from: 1, to: half },
    { from: Math.min(half + 1, totalP), to: totalP }
  ]

  renderThumbnailsBackground()
}

const renderThumbnailsBackground = async () => {
  for (const pageObj of pages.value) {
    await new Promise(resolve => setTimeout(resolve, 20))
    if (pdfjsDoc.value) {
      pageObj.thumbnail = await renderPageThumbnail(pdfjsDoc.value, pageObj.pageNum)
    } else {
      const fallbackCanvas = document.createElement("canvas")
      fallbackCanvas.width = 120
      fallbackCanvas.height = 160
      const ctx = fallbackCanvas.getContext("2d")
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, 120, 160)
      ctx.fillStyle = "#F43230"
      ctx.font = "bold 14px sans-serif"
      ctx.textAlign = "center"
      ctx.fillText(`Page ${pageObj.pageNum}`, 60, 80)
      pageObj.thumbnail = fallbackCanvas.toDataURL("image/png")
    }
    pageObj.isLoading = false
  }
}

const processSplit = async () => {
  try {
    isProcessing.value = true
    await nextTick()

    const baseName = currentFileName.value.replace(/\.pdf$/i, '') || 'document'

    if (mainTab.value === 'range') {
      if (mergeAllRanges.value) {
        // Merge all defined ranges into a single PDF
        const mergedPdf = await PDFDocument.create()
        for (const range of computedRanges.value) {
          const indices = range.pages.map(p => p.pageNum - 1)
          const copied = await mergedPdf.copyPages(pdfLibDoc.value, indices)
          copied.forEach(p => mergedPdf.addPage(p))
        }
        const bytes = await mergedPdf.save()
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName}_ranges_merged.pdf`)
      } else {
        // Output separate PDFs for each range
        if (computedRanges.value.length === 1) {
          const range = computedRanges.value[0]
          const singlePdf = await PDFDocument.create()
          const copied = await singlePdf.copyPages(pdfLibDoc.value, range.pages.map(p => p.pageNum - 1))
          copied.forEach(p => singlePdf.addPage(p))
          const bytes = await singlePdf.save()
          downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName}_range_${range.from}-${range.to}.pdf`)
        } else {
          const zip = new JSZip()
          for (let i = 0; i < computedRanges.value.length; i++) {
            const range = computedRanges.value[i]
            const rangePdf = await PDFDocument.create()
            const copied = await rangePdf.copyPages(pdfLibDoc.value, range.pages.map(p => p.pageNum - 1))
            copied.forEach(p => rangePdf.addPage(p))
            const bytes = await rangePdf.save()
            zip.file(`${baseName}_range_${i + 1}_pages_${range.from}-${range.to}.pdf`, bytes)
          }
          const content = await zip.generateAsync({ type: 'blob' })
          downloadBlob(content, `${baseName}_split_ranges.zip`)
        }
      }
    } else {
      // PAGES TAB
      if (extractMode.value === 'selected') {
        const pageNums = selectedPageNumbers.value
        if (pageNums.length === 0) return

        const extractedPdf = await PDFDocument.create()
        const copied = await extractedPdf.copyPages(pdfLibDoc.value, pageNums.map(p => p - 1))
        copied.forEach(p => extractedPdf.addPage(p))
        const bytes = await extractedPdf.save()
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName}_extracted_pages.pdf`)
      } else {
        // Extract all into ZIP
        const zip = new JSZip()
        for (const p of pages.value) {
          const singlePdf = await PDFDocument.create()
          const [copied] = await singlePdf.copyPages(pdfLibDoc.value, [p.pageNum - 1])
          singlePdf.addPage(copied)
          const bytes = await singlePdf.save()
          zip.file(`${baseName}_page_${p.pageNum}.pdf`, bytes)
        }
        const content = await zip.generateAsync({ type: 'blob' })
        downloadBlob(content, `${baseName}_all_pages.zip`)
      }
    }
  } catch (e) {
    console.error(e)
    alert('Error splitting PDF: ' + e.message)
  } finally {
    isProcessing.value = false
  }
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const onFileChange = (e) => {
  if (e.target.files.length) processFile(e.target.files[0])
  e.target.value = ''
}

const resetAll = () => {
  pages.value = []
  pdfLibDoc.value = null
  pdfjsDoc.value = null
  currentFileName.value = ''
}

const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; }
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; }
const onDragOver = (e) => { e.preventDefault(); }
const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer.files.length) processFile(e.dataTransfer.files[0])
}
</script>

<style scoped>
button:disabled {
  transform: none !important;
}
</style>
